import { getUser, getUsers, paginatedSearchForUsers, searchUsers, updateUser } from './http/access-ws/users';
import { getFirmMemberSecurityPackage, getUserSecurityPackage } from './http/users';
import { ChronoUnit, Instant } from '@js-joda/core';
import type { DefaultError, InfiniteData, QueryObserverOptions, QueryClient } from '@tanstack/query-core';
import type { UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import type { Access, SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import { ONE_MINUTE } from '@thinkalpha/ui-core/util/timeConstants';
import type { DetailedUser, PaginatedUserSearchResponse, User } from '@thinkalpha/ui-types/types/admin/users';
import type { UpdateUser } from 'src/lib/user/types';
import type { z } from 'zod';

// Constants
const USERS = 'users';
const SECURITY_PACKAGE = 'security-package';
const USER_SEARCH = 'user-search';
const PAGINATED = 'paginated';
export const PAGE_SIZE = 50;

// Select functions - hoisted to module scope to prevent re-renders
const selectSecurityPackageRoles = (data: z.infer<typeof SecurityPackage>): string[] =>
    data.roles.map((role) => role.role);

const selectSecurityPackageAccessList = (data: z.infer<typeof SecurityPackage>): z.infer<typeof Access>[] =>
    data.accessList;

export const getBulkUsers = getUsers;

export const getSearchUsersQuery = (search: string): UseQueryOptions<User[]> => ({
    queryKey: [USER_SEARCH, search],
    queryFn: () => searchUsers(search),
});

export const getUserQuery = (userId: string | undefined): UseQueryOptions<DetailedUser, Error, DetailedUser> => {
    return {
        queryKey: [USERS, userId],
        queryFn: () => getUser(userId!),
        enabled: Boolean(userId),
    };
};

export const getUserSecurityPackageQuery = (
    userId: string | undefined,
    /**
     * When provided, enables fast-polling (500ms) while a subscription is `pending_payment_method`
     * and its `lastFailedAt` still matches this baseline. Once `lastFailedAt` changes, the backend
     * has processed the payment outcome and fast-polling stops.
     */
    knownLastFailedAt?: Instant | null,
): QueryObserverOptions<z.infer<typeof SecurityPackage>, DefaultError> => ({
    queryKey: [USERS, userId, SECURITY_PACKAGE],
    queryFn: () => getUserSecurityPackage(userId!),
    staleTime: (q) =>
        q.state.data ? Math.max(0, Instant.now().until(q.state.data.expiration, ChronoUnit.MILLIS)) : ONE_MINUTE * 5,
    enabled: Boolean(userId),
    refetchIntervalInBackground: true,
    refetchInterval: (q) => {
        if (q.state.data) {
            if (q.state.data.subscriptions.some((sub) => sub.status === 'pending_activation')) {
                return 500;
            }
            if (
                knownLastFailedAt !== undefined &&
                q.state.data.subscriptions.some((sub) => {
                    if (sub.status !== 'pending_payment_method') return false;
                    const current = sub.lastFailedAt ?? null;
                    return current === null
                        ? knownLastFailedAt === null
                        : knownLastFailedAt !== null && current.equals(knownLastFailedAt);
                })
            ) {
                return 500;
            }
            return Math.max(0, Instant.now().until(q.state.data.expiration, ChronoUnit.MILLIS));
        }
        return false;
    },
});

export const getFirmMemberSecurityPackageQuery = (
    firmId: string | undefined,
    memberId: string | undefined,
): QueryObserverOptions<z.infer<typeof SecurityPackage>, DefaultError> => ({
    queryKey: [USERS, memberId, SECURITY_PACKAGE, 'firm', firmId],
    queryFn: () => getFirmMemberSecurityPackage(firmId!, memberId!),
    staleTime: (q) =>
        q.state.data ? Math.max(0, Instant.now().until(q.state.data.expiration, ChronoUnit.MILLIS)) : ONE_MINUTE * 5,
    enabled: Boolean(firmId) && Boolean(memberId),
    refetchIntervalInBackground: true,
    refetchInterval: (q) =>
        q.state.data ? Math.max(0, Instant.now().until(q.state.data.expiration, ChronoUnit.MILLIS)) : false,
});

export const getUsersPaginatedQuery = (
    userSearchValue: string,
    enabled: boolean,
): UseInfiniteQueryOptions<
    PaginatedUserSearchResponse,
    Error,
    InfiniteData<PaginatedUserSearchResponse>,
    [string, string, string],
    number
> => {
    return {
        initialPageParam: 0,
        queryKey: [USER_SEARCH, PAGINATED, userSearchValue],
        queryFn: ({ pageParam = 0 }: { pageParam: number }) =>
            paginatedSearchForUsers(userSearchValue, {
                $top: PAGE_SIZE,
                $skip: pageParam,
                $count: true,
            }),
        getNextPageParam: (
            lastPage: PaginatedUserSearchResponse,
            _allPages: PaginatedUserSearchResponse[],
            lastPageParam: number,
        ) => {
            if (lastPageParam + PAGE_SIZE >= lastPage.count) {
                return undefined;
            }
            const remainingItems = lastPage.count - lastPageParam;
            return remainingItems < PAGE_SIZE ? lastPageParam + remainingItems : lastPageParam + PAGE_SIZE;
        },
        enabled,
    };
};

// API returns all roles associated with the user. This is separate from the user.roles present on the user API response.
export const getUserRolesQuery = (
    userId: string | undefined,
): QueryObserverOptions<z.infer<typeof SecurityPackage>, DefaultError, string[]> => ({
    ...getUserSecurityPackageQuery(userId),
    select: selectSecurityPackageRoles,
});

export const getUserAccessListQuery = (
    userId: string | undefined,
): QueryObserverOptions<z.infer<typeof SecurityPackage>, DefaultError, z.infer<typeof Access>[]> => ({
    ...getUserSecurityPackageQuery(userId),
    select: selectSecurityPackageAccessList,
});

export const getUpdateUserMutation = (
    queryClient: QueryClient,
    mutationOptions?: UseMutationOptions<User, Error, UpdateUser>,
): UseMutationOptions<User, Error, UpdateUser> => {
    const { onSuccess, onError, onSettled, ...restMutationOptions } = mutationOptions || {};

    return {
        mutationFn: updateUser,
        onSuccess: (data, variables, context, mutationContext) => {
            updateUserQueryCache(queryClient, data);
            onSuccess?.(data, variables, context, mutationContext);
        },
        onError: (error, variables, context, mutationContext) => {
            onError?.(error, variables, context, mutationContext);
        },
        onSettled: (data, error, variables, context, mutationContext) => {
            onSettled?.(data, error, variables, context, mutationContext);
        },
        ...restMutationOptions,
    };
};

// Invalidation

export const invalidateUserQuery = (queryClient: QueryClient, userId: string) => {
    return queryClient.invalidateQueries({ queryKey: [USERS, userId] });
};

export const invalidateUserSecurityPackageQuery = (queryClient: QueryClient, userId: string) => {
    return queryClient.invalidateQueries({ queryKey: [USERS, userId, SECURITY_PACKAGE] });
};

/**
 * Invalidates ALL security-package queries regardless of userId.
 * This is needed when roles change (e.g. after subscription activation) because different
 * callers may use different userId values (e.g. `'me'` vs the actual UUID).
 */
export const invalidateAllSecurityPackageQueries = (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key.length >= 3 && key[0] === USERS && key[2] === SECURITY_PACKAGE;
        },
    });
};

export const updateUserQueryCache = (queryClient: QueryClient, updatedUser: User) => {
    return queryClient.setQueryData([USERS, updatedUser.id], () => ({ ...updatedUser }));
};
